import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { dispatchWithToast } from "../../../Utils";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { updateReportAsync } from "../../../../slices/service/reports";
import { useDispatch } from "react-redux";
import { PMField } from "../../../utils/PMField";
import { Link, useNavigate } from "react-router-dom";
import { OrdersStacklist } from "../../orders/orders-stack-list";
import PMSwitch from "../../../utils/PMSwitch";
import { PMNotice } from "../../../utils/notifications";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { customerLockBlocksSubmit } from "../../../../contexts/CustomerLockTracker";
import { fetchReportOrderPreview, generateInvoiceDescription } from "../../../../api/service/report";

/** Section card — same visual language as `SideSection` in `pages/dashboard/report.js` */
function OrderDialogSection({
  title,
  description,
  headerRight,
  children,
  contentClassName = "space-y-4",
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1 pr-2">
          <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
            {title}
          </h2>
          {description ? (
            <p className="mt-1 text-sm text-slate-600">{description}</p>
          ) : null}
        </div>
        {headerRight ? (
          <div className="shrink-0 self-start sm:self-center">{headerRight}</div>
        ) : null}
      </div>
      <div className={contentClassName}>{children}</div>
    </section>
  );
}

export const ReportOrderDialog = (props) => {
  const { onClose, open, report } = props;
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const formatHourDecimal = (value) => {
    const s = Number(value || 0).toFixed(1);
    const lang = i18n.resolvedLanguage || i18n.language || "";
    if (lang === "nb" || lang === "no" || lang.startsWith("nb")) {
      return s.replace(".", ",");
    }
    return s;
  };

  const [orderPreview, setOrderPreview] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [selectedHours, setSelectedHours] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedPriceLines, setSelectedPriceLines] = useState([]);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [hoursSettings, setHoursSettings] = useState({
    discount: 0,
    manualQuantity: null,
    /** actual | ceil_half | ceil_hour | nearest_half | nearest_hour — matches API hours_settings.roundingMode */
    roundingMode: "actual",
    /** grouped = one line per activity (merged); each = one line per timeregistrering — API hours_settings.hourLines */
    hourLines: "each",
  });
  const [invoiceLanguage, setInvoiceLanguage] = useState("no"); // Default to Norwegian

  // Change isLoading depending on props.isLoading
  useEffect(() => {
    setIsLoading(props.isLoading);
  }, [props.isLoading]);

  function removeHtmlTags(text) {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(text, "text/html");
    return htmlDoc.body.textContent || "";
  }

  const getOrderPreview = async (payload) => {
    setIsLoading(true);
    return await fetchReportOrderPreview(payload)
      .then((response) => {
        console.log(response);
        setOrderPreview(response?.data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      getOrderPreview({ id: report?.id });
      // Initialize selected hours and items when dialog opens
      if (orderPreview?.hours) {
        setSelectedHours(orderPreview.hours.map((h) => h.id));
      }
      if (orderPreview?.order_items) {
        setSelectedItems(orderPreview.order_items.map((i) => i.id));
      }
    }
  }, [open, report]);

  useEffect(() => {
    // Update selected hours and items when orderPreview changes
    // Only select hours that are NOT included in price lines
    if (orderPreview?.hours) {
      const billableHours = orderPreview.hours
        .filter((h) => !h.included_in_agreed_price)
        .map((h) => h.id);
      setSelectedHours(billableHours);
    }
    // Only select items with quantity_used > 0
    if (orderPreview?.order_items) {
      const itemsWithUsage = orderPreview.order_items
        .filter((item) => (item.quantity_used || 0) > 0)
        .map((i) => i.id);
      setSelectedItems(itemsWithUsage);
    }
    if (orderPreview?.price_lines) {
      const agreedLines = orderPreview.price_lines
        .filter((pl) => pl.is_agreed_price_line)
        .map((pl) => pl.id);
      setSelectedPriceLines(agreedLines);
    }
  }, [orderPreview]);

  const initialValues = {
    id: orderPreview?.id,
    create_order: true,
    order_number: orderPreview?.order_number,
    invoiceText: orderPreview?.invoice_description,
    invoiceTitle: orderPreview?.invoice_title,
    invoice_customer: report?.invoice_customer,
    calculate_km_google_maps:
      report?.trips?.length > 0
        ? orderPreview?.google_maps_calculated_distance > 0
          ? true
          : false
        : true,
    calculate_km_trips: report?.trips?.length > 0 ? true : false,
    calculate_km:
      orderPreview?.trips?.length > 0 ||
      orderPreview?.google_maps_calculated_distance > 0,
    calculate_hours: (orderPreview?.hours || []).some(
      (h) => !h.included_in_agreed_price
    ),
    add_order_items: orderPreview?.order_items?.filter((item) => (item.quantity_used || 0) > 0).length > 0 ? true : false,
    /** Egen ordregruppe «Tilleggsarbeider» for timer og (ved behov) varelinjer ved tilbudslinjer */
    add_additional_work_group: false,
    items: [],
    submit: null,
  };

  function anyErrors() {
    // Check if there is any errors with type "create_order" in report.operations
    if (report?.operations) {
      const errors = report.operations.filter(
        (operation) => operation.name === "create_order"
      );
      return errors.length > 0;
    }
  }

  function Errors() {
    // Return errors with type "create_order" in report.operations
    if (report?.operations) {
      const errors = report.operations.filter(
        (operation) => operation.name === "create_order"
      );
      return (
        <div className="col-span-6 sm:col-span-6">
          <div className="text-red-500">
            {errors.map((error, index) => (
              <div key={index}>{error.message}</div>
            ))}
          </div>
        </div>
      );
    }
  }

  function selectedTotalHours() {
    if (orderPreview?.hours && selectedHours.length > 0) {
      // Only count hours that are NOT included in price lines
      const hours = orderPreview.hours
        .filter((h) => selectedHours.includes(h.id) && !h.included_in_agreed_price)
        .map((hour) => Number(hour.time));
      const total = hours.reduce((a, b) => a + b, 0);
      return total;
    }
    return 0;
  }

  function totalBillableHours() {
    // Total hours that are NOT included in price lines (can be billed separately)
    if (orderPreview?.hours) {
      const hours = orderPreview.hours
        .filter((h) => !h.included_in_agreed_price)
        .map((hour) => Number(hour.time));
      const total = hours.reduce((a, b) => a + b, 0);
      return total;
    }
    return 0;
  }

  function totalPriceLineHours() {
    // Total hours that ARE included in price lines
    if (orderPreview?.hours) {
      const hours = orderPreview.hours
        .filter((h) => h.included_in_agreed_price === true)
        .map((hour) => Number(hour.time));
      const total = hours.reduce((a, b) => a + b, 0);
      return total;
    }
    return 0;
  }

  const handleGenerateInvoiceDescription = async (formik) => {
    // Validate before generating
    const reportText = report?.report || report?.description || "";
    const plainText = removeHtmlTags(reportText);
    
    if (!reportText || plainText.trim().length < 10) {
      setValidationMessage(
        "Rapporten må ha minst 10 tegn med innhold for å generere beskrivelse. " +
        "Vennligst legg til mer informasjon i rapporten først."
      );
      setShowValidationDialog(true);
      return;
    }

    setIsGeneratingDescription(true);
    try {
      const response = await generateInvoiceDescription({
        id: report?.id,
        report: { report: reportText, language: invoiceLanguage },
      });

      if (response?.data?.invoice_description) {
        formik.setFieldValue("invoiceText", response.data.invoice_description);
        toast.success("Beskrivelse generert");
      } else if (response?.data?.errors) {
        setValidationMessage(
          response.data.errors.join(", ") || "Kunne ikke generere beskrivelse"
        );
        setShowValidationDialog(true);
      } else {
        toast.error("Kunne ikke generere beskrivelse");
      }
    } catch (error) {
      console.error("Error generating invoice description:", error);
      const errorMessage = error?.response?.data?.errors?.join(", ") || 
                          error?.response?.data?.error ||
                          "Kunne ikke generere beskrivelse. Prøv igjen senere.";
      setValidationMessage(errorMessage);
      setShowValidationDialog(true);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const applyHoursRoundingPreview = (quantity, mode) => {
    const q = Number(quantity) || 0;
    if (q <= 0) return 0;
    const m = mode || "actual";
    switch (m) {
      case "actual":
      case "none":
        return Math.round(q * 1e6) / 1e6;
      case "ceil_half":
        return Math.ceil(q * 2) / 2;
      case "ceil_hour":
        return Math.ceil(q);
      case "nearest_half":
        return Math.round(q * 2) / 2;
      case "nearest_hour":
        return Math.round(q);
      default:
        return q;
    }
  };

  const calculateHoursQuantity = () => {
    const mode = hoursSettings.roundingMode || "actual";
    if (hoursSettings.manualQuantity !== null && hoursSettings.manualQuantity !== "") {
      return applyHoursRoundingPreview(
        parseFloat(String(hoursSettings.manualQuantity).replace(",", ".")),
        mode
      );
    }
    return applyHoursRoundingPreview(selectedTotalHours(), mode);
  };

  const navigate = useNavigate();

  const hasOrdersAlready =
    (report?.orders?.length || 0) > 0 || Boolean(orderPreview?.has_existing_orders);

  const handleRefresh = (payload) => {
    // API returns { data: <updated report>, order: { id, ... } } (legacy: { data: <order> }).
    const updatedReport =
      payload?.id === report?.id
        ? payload
        : payload?.data?.id === report?.id
          ? payload.data
          : null;

    const orderId =
      payload?.order?.id ??
      (payload?.data?.id && payload.data.id !== report?.id
        ? payload.data.id
        : null);

    if (updatedReport) {
      props.onRefresh(updatedReport);
    } else if (report?.id) {
      // Legacy API may return { data: <order> } without the updated report.
      // Spread the current report — ReportLayout replaces state wholesale when data.id is set.
      props.onRefresh({
        ...report,
        processed: true,
        under_process: true,
        updated_at: new Date().toISOString(),
      });
    } else {
      props.onRefresh();
    }

    if (orderId) {
      setTimeout(() => {
        navigate(`/dashboard/orders/${orderId}`);
      }, 200);
    }
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 max-sm:flex max-sm:flex-col max-sm:overflow-hidden sm:overflow-y-auto">
            <div className="flex min-h-full flex-1 justify-center p-0 text-center max-sm:min-h-0 max-sm:flex-1 sm:items-center sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative flex max-h-[100dvh] w-full max-w-md transform flex-col overflow-hidden border border-slate-200/90 bg-slate-50 text-left align-middle shadow-xl transition-all max-sm:max-h-[100dvh] max-sm:min-h-0 max-sm:max-w-none max-sm:flex-1 max-sm:rounded-none max-sm:border-0 sm:max-h-[min(92vh,1200px)] sm:max-w-6xl lg:max-w-7xl sm:rounded-[2rem]">
                  <div className="flex min-h-0 flex-1 flex-col sm:min-h-0">
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain bg-slate-50 px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
                  <div className="mt-0 sm:mt-0">
                    <div className="mb-5">
                      <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 shadow-sm ring-1 ring-slate-200/80">
                        {t("service.report.order_dialog.dialog_eyebrow")}
                      </div>
                      <Dialog.Title
                        as="h3"
                        className="mt-3 text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl"
                      >
                        {hasOrdersAlready
                          ? t("service.report.order_dialog.title_additional")
                          : t("service.report.order_dialog.title_create")}
                      </Dialog.Title>
                    </div>

                    {isLoading ? (
                      <ResourceLoading />
                    ) : !report?.id ? (
                      "Missing ID"
                    ) : !report?.finished ? (
                      <div className="mx-auto py-10 text-red-500">
                        {" "}
                        This report is not finished yet.{" "}
                      </div>
                    ) : (
                      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          {t("service.report.order_dialog.orders_heading")}
                        </div>
                        <div className="mt-3">
                          <OrdersStacklist
                            module_param={report?.id}
                            module="report"
                            filters={[
                              {
                                name: "report",
                                value: report?.id,
                              },
                            ]}
                          />
                        </div>
                      </div>
                    )}

                    {isLoading ? (
                      <ResourceLoading />
                    ) : !orderPreview?.id ? (
                      <PMNotice
                        title="Kunne ikke laste inn ordredetaljer"
                        description="Prøv igjen senere eller kontakt support."
                        severity={"error"}
                      />
                    ) : (
                      <>
                        {orderPreview?.warnings && orderPreview.warnings.length > 0 && (
                          <div className="mb-4">
                            {orderPreview.warnings.map((warning, index) => (
                              <PMNotice
                                key={index}
                                title="Advarsel"
                                description={warning}
                                severity={"warning"}
                              />
                            ))}
                          </div>
                        )}
                        {!report?.invoiced ? (
                      <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        // validationSchema={Yup.object({
                        //   status: Yup.string().required(
                        //     t("You need to pick a status option")
                        //   ),
                        // })}
                        onSubmit={(values, helpers) => {
                          try {
                            // Add selected hours and items to the payload
                            const submitValues = {
                              ...values,
                              selected_hours: values.calculate_hours
                                ? selectedHours
                                : [],
                              selected_items: values.add_order_items
                                ? selectedItems
                                : [],
                              selected_price_lines: selectedPriceLines,
                              hours_settings: values.calculate_hours
                                ? hoursSettings
                                : null,
                            };

                            dispatchWithToast(
                              dispatch(updateReportAsync(submitValues)),
                              (data) => handleRefresh(data),
                              props.onClose
                            );
                            helpers.setStatus({ success: true });
                            helpers.setSubmitting(false);
                            helpers.resetForm(initialValues);
                          } catch (err) {
                            toast.error("Something went wrong...");
                            console.error(err);
                            helpers.setStatus({ success: false });
                            helpers.setErrors({ submit: err.message });
                            helpers.setSubmitting(false);
                          }
                        }}
                      >
                        {(formik) => (
                          <Form>
                            <div className="space-y-6">
                            {orderPreview?.has_existing_orders && (
                              <div>
                                <PMNotice
                                  title={t("service.report.order_dialog.follow_up_notice_title")}
                                  description={t(
                                    "service.report.order_dialog.follow_up_notice_body"
                                  )}
                                  severity="info"
                                />
                              </div>
                            )}
                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-x-6 lg:gap-y-6 lg:items-start">
                              <div className="min-w-0 lg:col-span-5 xl:col-span-4">
                                <OrderDialogSection title={t("service.report.order_dialog.section_invoice")}>
                                  <CustomerSelector
                                    selected={orderPreview?.invoice_customer}
                                    label="Faktura kunde"
                                    name="invoice_customer"
                                    disabled
                                    {...formik}
                                  />
                                  <PMField
                                    name="order_number"
                                    type="text"
                                    label={t("Order Number")}
                                    readOnly={true}
                                    {...formik}
                                  />
                                  <PMField
                                    name="invoiceTitle"
                                    type="text"
                                    label={t("Invoice Title")}
                                    readOnly={false}
                                    {...formik}
                                  />
                                  <div>
                                    <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                      <label className="block text-sm font-medium text-slate-700">
                                        {t("Invoice Text")}
                                      </label>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <select
                                          value={invoiceLanguage}
                                          onChange={(e) => setInvoiceLanguage(e.target.value)}
                                          className="rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400/40"
                                          disabled={isGeneratingDescription}
                                        >
                                          <option value="no">Norsk</option>
                                          <option value="en">English</option>
                                          <option value="sv">Svenska</option>
                                          <option value="da">Dansk</option>
                                          <option value="de">Deutsch</option>
                                          <option value="fr">Français</option>
                                          <option value="es">Español</option>
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => handleGenerateInvoiceDescription(formik)}
                                          disabled={isGeneratingDescription}
                                          className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                          {isGeneratingDescription
                                            ? t("service.report.order_dialog.generating_invoice")
                                            : t("service.report.order_dialog.generate_invoice")}
                                        </button>
                                      </div>
                                    </div>
                                    <PMField
                                      multiline
                                      name="invoiceText"
                                      type="text"
                                      rows={4}
                                      readOnly={false}
                                      {...formik}
                                    />
                                  </div>
                                </OrderDialogSection>
                              </div>

                              <div className="min-w-0 lg:col-span-7 xl:col-span-8">
                              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-6 xl:items-start">
                              <div className="min-w-0 space-y-6">

                              <OrderDialogSection title={t("service.report.order_dialog.section_km")}>
                                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                                  <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between">
                                    <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                                      <PMSwitch
                                        name="calculate_km"
                                        label="Ja"
                                        onChange={() => {
                                          formik.setFieldValue(
                                            "calculate_km_google_maps",
                                            false
                                          );
                                          formik.setFieldValue(
                                            "calculate_km_trips",
                                            false
                                          );
                                        }}
                                        {...formik}
                                      />
                                      {formik?.values?.calculate_km && (
                                        <>
                                          <PMSwitch
                                            name="calculate_km_google_maps"
                                            label={
                                              "With Google Maps (" +
                                              orderPreview?.google_maps_calculated_distance +
                                              " km)"
                                            }
                                            onChange={() => {
                                              formik.setFieldValue(
                                                "calculate_km_trips",
                                                false
                                              );
                                            }}
                                            {...formik}
                                          />
                                          <PMSwitch
                                            disabled={
                                              orderPreview?.trips?.length > 0
                                                ? false
                                                : true
                                            }
                                            name="calculate_km_trips"
                                            label={
                                              "By trips (" +
                                              orderPreview?.trips?.length +
                                              " turer registrert)"
                                            }
                                            onChange={() => {
                                              formik.setFieldValue(
                                                "calculate_km_google_maps",
                                                false
                                              );
                                            }}
                                            {...formik}
                                          />
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </OrderDialogSection>

                              {orderPreview?.price_lines &&
                              !orderPreview?.has_existing_orders &&
                              orderPreview.price_lines.filter(pl => pl.is_agreed_price_line).length > 0 && (
                                <OrderDialogSection
                                  title={t("service.report.order_dialog.section_price_lines")}
                                  description={t("service.report.order_dialog.price_lines_select_hint")}
                                >
                                  <p className="text-sm text-slate-600">
                                    {t("service.report.order_dialog.price_lines_intro")}
                                  </p>
                                  <div className="space-y-2">
                                    {orderPreview.price_lines
                                      .filter(pl => pl.is_agreed_price_line)
                                      .map((priceLine) => {
                                        const includedHours = orderPreview.hours?.filter(
                                          h => h.included_in_agreed_price === true &&
                                               h.price_line?.id === priceLine.id
                                        ) || [];
                                        const includedHoursTotal = includedHours.reduce((sum, h) => sum + Number(h.time || 0), 0);
                                        const isSelected = selectedPriceLines.includes(priceLine.id);

                                        return (
                                          <label
                                            key={priceLine.id}
                                            className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-slate-300"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setSelectedPriceLines([...selectedPriceLines, priceLine.id]);
                                                } else {
                                                  setSelectedPriceLines(
                                                    selectedPriceLines.filter((id) => id !== priceLine.id)
                                                  );
                                                }
                                              }}
                                              className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                                            />
                                            <div className="min-w-0 flex-1">
                                              <div className="text-sm font-medium text-slate-900">
                                                {priceLine.title || priceLine.activity_item?.displayTitle || "Prislinje"}
                                              </div>
                                              <div className="mt-1 text-xs text-slate-500">
                                                {priceLine.agreed_price_incl_vat
                                                  ? `${Number(priceLine.agreed_price_incl_vat).toFixed(2).replace(".", ",")} kr (inkl. mva)`
                                                  : priceLine.agreed_price
                                                  ? `${Number(priceLine.agreed_price).toFixed(2).replace(".", ",")} kr (eks. mva)`
                                                  : "Ingen pris"}
                                                {priceLine.agreed_hours && ` — Avtalt: ${priceLine.agreed_hours} timer`}
                                                {includedHoursTotal > 0 && ` — Registrert: ${includedHoursTotal.toFixed(1)} timer`}
                                              </div>
                                              {includedHours.length > 0 && (
                                                <div className="mt-3 border-t border-slate-100 pt-3">
                                                  <div className="mb-1 text-xs font-medium text-slate-600">
                                                    {t("service.report.order_dialog.price_lines_included_hours")}
                                                  </div>
                                                  <div className="space-y-1">
                                                    {includedHours.map((hour) => (
                                                      <div key={hour.id} className="flex items-center gap-2 text-xs text-slate-600">
                                                        <span className="text-slate-400">•</span>
                                                        <span>{hour.displayTitle || hour.activity?.displayTitle || `Time ${hour.id}`}</span>
                                                        <span className="text-slate-500">({Number(hour.time).toFixed(1)} t)</span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </label>
                                        );
                                      })}
                                  </div>
                                  <div className="rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-700">
                                    {t("service.report.order_dialog.price_lines_footer_note", {
                                      hours: totalPriceLineHours().toFixed(1),
                                    })}
                                  </div>
                                </OrderDialogSection>
                              )}

                              <OrderDialogSection
                                title={t("service.report.order_dialog.section_order_lines")}
                                description={t(
                                  "service.report.order_dialog.section_order_lines_subtitle",
                                  {
                                    selected: selectedItems.length,
                                    total:
                                      orderPreview?.order_items?.filter(
                                        (item) => (item.quantity_used || 0) > 0
                                      ).length || 0,
                                  }
                                )}
                                headerRight={
                                  <PMSwitch
                                    name="add_order_items"
                                    hideLabel
                                    ariaLabel={t(
                                      "service.report.order_dialog.toggle_order_lines_aria"
                                    )}
                                    onChange={() => {}}
                                    {...formik}
                                  />
                                }
                              >
                                {formik?.values?.add_order_items && (
                                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                                    <label className="mb-3 block text-sm font-medium text-slate-700">
                                      {t("service.report.order_dialog.pick_order_lines")}
                                    </label>
                                    <div className="max-h-48 space-y-1 overflow-y-auto">
                                      {orderPreview?.order_items &&
                                        orderPreview.order_items
                                          .filter((item) => (item.quantity_used || 0) > 0)
                                          .length > 0 &&
                                        orderPreview.order_items
                                          .filter((item) => (item.quantity_used || 0) > 0)
                                          .map((item) => (
                                          <label
                                            key={item.id}
                                            className="flex cursor-pointer items-center rounded-xl p-2 transition hover:bg-white"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={selectedItems.includes(item.id)}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setSelectedItems([
                                                    ...selectedItems,
                                                    item.id,
                                                  ]);
                                                } else {
                                                  setSelectedItems(
                                                    selectedItems.filter(
                                                      (id) => id !== item.id
                                                    )
                                                  );
                                                }
                                              }}
                                              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                                            />
                                            <div className="ml-3 min-w-0 flex-1">
                                              <div className="text-sm font-medium text-slate-900">
                                                {item.title || item.product?.title || `Linje ${item.id}`}
                                              </div>
                                              <div className="text-xs text-slate-500">
                                                Antall: {item.quantity_used || item.quantity} × {item.sales_price} kr
                                                {item.quantity_used && item.quantity_used !== item.quantity && (
                                                  <span className="ml-1 text-slate-400">
                                                    (av {item.quantity})
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </label>
                                        ))}
                                      {(!orderPreview?.order_items ||
                                        orderPreview.order_items.filter((item) => (item.quantity_used || 0) > 0).length === 0) && (
                                        <div className="p-2 text-sm italic text-slate-500">
                                          {t("service.report.order_dialog.no_consumption_lines")}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </OrderDialogSection>

                              <OrderDialogSection title={t("service.report.order_dialog.section_order_group")}>
                                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                                  <PMSwitch
                                    name="add_additional_work_group"
                                    label={t("service.report.order_dialog.add_tilleggsarbeider_group_label")}
                                    onChange={() => {}}
                                    {...formik}
                                  />
                                  <p className="mt-3 text-xs leading-relaxed text-slate-600">
                                    {t("service.report.order_dialog.add_tilleggsarbeider_group_hint")}
                                  </p>
                                </div>
                              </OrderDialogSection>

                              {/* Timer — egen kolonne på xl+ */}
                              </div>
                              <div className="min-w-0 space-y-6">
                              {orderPreview?.id && (
                                <OrderDialogSection
                                  title={t("service.report.order_dialog.section_hours")}
                                  description={
                                    totalBillableHours() > 0
                                      ? t("service.report.order_dialog.section_hours_subtitle", {
                                          selected: formatHourDecimal(selectedTotalHours()),
                                          total: formatHourDecimal(totalBillableHours()),
                                        })
                                      : t("service.report.order_dialog.section_hours_subtitle_manual")
                                  }
                                  headerRight={
                                    <PMSwitch
                                      name="calculate_hours"
                                      hideLabel
                                      ariaLabel={t(
                                        "service.report.order_dialog.toggle_hours_aria"
                                      )}
                                      onChange={() => {}}
                                      {...formik}
                                    />
                                  }
                                >
                                  {formik?.values?.calculate_hours && (
                                    <div className="space-y-4">
                                      {totalBillableHours() <= 0 && (
                                        <PMNotice
                                          severity="info"
                                          title={t("service.report.order_dialog.hours_manual_notice_title")}
                                          description={t("service.report.order_dialog.hours_manual_notice_body")}
                                        />
                                      )}
                                      {(orderPreview?.hours || []).filter((h) => !h.included_in_agreed_price).length > 0 && (
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                                          <label className="mb-3 block text-sm font-medium text-slate-700">
                                            {t("service.report.order_dialog.pick_billable_hours")}
                                          </label>
                                          <div className="max-h-48 space-y-1 overflow-y-auto">
                                            {(orderPreview?.hours || [])
                                              .filter((h) => !h.included_in_agreed_price)
                                              .map((hour) => {
                                                return (
                                                  <label
                                                    key={hour.id}
                                                    className="flex cursor-pointer items-center rounded-xl p-2 transition hover:bg-white"
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={selectedHours.includes(hour.id)}
                                                      onChange={(e) => {
                                                        if (e.target.checked) {
                                                          setSelectedHours([
                                                            ...selectedHours,
                                                            hour.id,
                                                          ]);
                                                        } else {
                                                          setSelectedHours(
                                                            selectedHours.filter(
                                                              (id) => id !== hour.id
                                                            )
                                                          );
                                                        }
                                                      }}
                                                      className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                                                    />
                                                    <div className="ml-3 min-w-0 flex-1">
                                                      <div className="text-sm font-medium text-slate-900">
                                                        {hour.displayTitle || hour.activity?.displayTitle || `Time ${hour.id}`}
                                                      </div>
                                                      <div className="text-xs text-slate-500">
                                                        {Number(hour.time).toFixed(1)} timer
                                                      </div>
                                                    </div>
                                                  </label>
                                                );
                                              })}
                                          </div>
                                        </div>
                                      )}

                                      <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                        <label className="block text-sm font-medium text-slate-800">
                                          {t("service.report.order_dialog.hours_settings_heading")}
                                        </label>

                                        <div>
                                          <label className="mb-1 block text-sm text-slate-600">
                                            {t("service.report.order_dialog.manual_hours_label")}
                                          </label>
                                          <input
                                            type="number"
                                            step="0.5"
                                            min="0"
                                            value={
                                              hoursSettings.manualQuantity === null
                                                ? ""
                                                : hoursSettings.manualQuantity
                                            }
                                            onChange={(e) => {
                                              setHoursSettings({
                                                ...hoursSettings,
                                                manualQuantity:
                                                  e.target.value === ""
                                                    ? null
                                                    : parseFloat(e.target.value),
                                              });
                                            }}
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                                            placeholder={t("service.report.order_dialog.manual_hours_placeholder")}
                                          />
                                        </div>

                                        <div>
                                          <label className="mb-1 block text-sm text-slate-600">
                                            {t("service.report.order_dialog.hours_rounding_label")}
                                          </label>
                                          <select
                                            value={hoursSettings.roundingMode || "actual"}
                                            onChange={(e) =>
                                              setHoursSettings({
                                                ...hoursSettings,
                                                roundingMode: e.target.value,
                                              })
                                            }
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                                          >
                                            <option value="actual">
                                              {t("service.report.order_dialog.hours_rounding_actual")}
                                            </option>
                                            <option value="ceil_half">
                                              {t("service.report.order_dialog.hours_rounding_ceil_half")}
                                            </option>
                                            <option value="nearest_half">
                                              {t("service.report.order_dialog.hours_rounding_nearest_half")}
                                            </option>
                                            <option value="ceil_hour">
                                              {t("service.report.order_dialog.hours_rounding_ceil_hour")}
                                            </option>
                                            <option value="nearest_hour">
                                              {t("service.report.order_dialog.hours_rounding_nearest_hour")}
                                            </option>
                                          </select>
                                          <p className="mt-1 text-xs text-slate-500">
                                            {t("service.report.order_dialog.hours_rounding_hint")}
                                          </p>
                                        </div>

                                        <div>
                                          <label className="mb-1 block text-sm text-slate-600">
                                            {t("service.report.order_dialog.hour_lines_label")}
                                          </label>
                                          <select
                                            value={hoursSettings.hourLines || "each"}
                                            onChange={(e) =>
                                              setHoursSettings({
                                                ...hoursSettings,
                                                hourLines: e.target.value,
                                              })
                                            }
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                                          >
                                            <option value="each">
                                              {t("service.report.order_dialog.hour_lines_each")}
                                            </option>
                                            <option value="grouped">
                                              {t("service.report.order_dialog.hour_lines_grouped")}
                                            </option>
                                          </select>
                                          <p className="mt-1 text-xs text-slate-500">
                                            {t("service.report.order_dialog.hour_lines_hint")}
                                          </p>
                                        </div>

                                        <div>
                                          <label className="mb-1 block text-sm text-slate-600">
                                            {t("service.report.order_dialog.hours_discount_label")}
                                          </label>
                                          <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            max="100"
                                            value={hoursSettings.discount}
                                            onChange={(e) => {
                                              setHoursSettings({
                                                ...hoursSettings,
                                                discount: parseFloat(e.target.value) || 0,
                                              });
                                            }}
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                                          />
                                        </div>

                                        <div className="rounded-xl bg-slate-50 p-3">
                                          <div className="text-sm text-slate-600">
                                            <span className="font-semibold text-slate-800">
                                              {t("service.report.order_dialog.hours_preview_heading")}
                                            </span>
                                          </div>
                                          <div className="mt-1 text-sm text-slate-900">
                                            {t("service.report.order_dialog.hours_preview_quantity", {
                                              hours: calculateHoursQuantity().toFixed(1),
                                            })}
                                            {hoursSettings.discount > 0 && (
                                              <span className="ml-2 text-slate-600">
                                                ({t("service.report.order_dialog.hours_preview_discount", {
                                                  pct: hoursSettings.discount,
                                                })})
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </OrderDialogSection>
                              )}
                              </div>
                              </div>
                              </div>

                              {anyErrors() && (
                                <div className="w-full lg:col-span-12">
                                  <Errors />
                                </div>
                              )}
                            </div>
                            <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:flex-wrap sm:items-center sm:justify-between max-sm:pb-6">
                              <button
                                type="button"
                                className="inline-flex justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/40"
                                onClick={onClose}
                              >
                                {t("Close")}
                              </button>
                              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
                                {(report?.orders || []).map((o) => (
                                  <Link
                                    key={o.id}
                                    to={"/dashboard/orders/" + o.id}
                                    className="inline-flex justify-center rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-white"
                                  >
                                    {t("service.report.order_dialog.open_order", {
                                      number: o.order_number ?? o.id,
                                    })}
                                  </Link>
                                ))}
                                <button
                                  disabled={
                                    anyErrors() ||
                                    customerLockBlocksSubmit(formik.values)
                                  }
                                  type="button"
                                  className="inline-flex justify-center rounded-xl border border-transparent bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400/50 disabled:cursor-not-allowed disabled:opacity-40"
                                  onClick={formik.submitForm}
                                >
                                  {hasOrdersAlready
                                    ? t("service.report.order_dialog.create_another")
                                    : t("service.report.order_dialog.create")}
                                </button>
                              </div>
                            </div>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    ) : (
                      <PMNotice
                        type="info"
                        title="This report is already invoiced"
                          />
                        )}
                      </>
                    )}
                  </div>
                  </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Validation Dialog */}
      <Transition.Root show={showValidationDialog} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowValidationDialog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg
                        className="h-6 w-6 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Kan ikke generere beskrivelse
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">{validationMessage}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                      onClick={() => setShowValidationDialog(false)}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
